/*
 * VoxPontOnboardingPage — /voxpont-start.
 *
 * The post-auth landing for VoxPont users. If they already chose a default
 * product it redirects there; otherwise it shows the product picker. Choosing a
 * product saves it as the default and navigates to that product.
 *
 * Brand note: like VoxPontAuthPage, this is a deliberate bespoke VoxPont brand
 * surface (navy + gold), not the HealthDesk theme. The pickable product cards
 * themselves use the shared MUI theme via SelectableChoiceCard.
 */

import React, { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import Loading from "components/Loading";
import { setVoxPontProduct } from "services/auth/userProductOrigin";
import { getVoxPontProductBySlug } from "features/voxpontProduct/voxpontProducts";
import VoxPontProductPicker from "components/voxpont/VoxPontProductPicker";
import VoxPontLogo from "components/voxpont/VoxPontLogo";
import { getReleasedVoxPontEngine } from "features/voxpontEngine/voxpontEngineRegistry";
const NAVY_DEEP = "#0C1E36";
const NAVY = "#122B4D";
const GOLD = "#F2C94C";

export default function VoxPontOnboardingPage() {
  const { user, userData, userLoading } = useAuth();
  const navigate = useNavigate();
  const [savingSlug, setSavingSlug] = useState(null);

  // Wait for auth + the user doc snapshot so we don't flash the picker before we
  // know whether a default product is already saved.
  if (userLoading || (user && userData === undefined)) {
    return <Loading page />;
  }

  if (!user) {
    return <Navigate to="/voxpont-auth" replace />;
  }

  const savedEngine = getReleasedVoxPontEngine(userData?.voxpontEngine);
  if (savedEngine) {
    return <Navigate to={savedEngine.route} replace />;
  }

  const savedProduct = getVoxPontProductBySlug(userData?.voxpontProduct);
  if (savedProduct) {
    return <Navigate to={savedProduct.route} replace />;
  }

  const handleSelect = async (slug) => {
    const product = getVoxPontProductBySlug(slug);
    if (!product || savingSlug) return;
    setSavingSlug(slug);
    try {
      await setVoxPontProduct(user.uid, slug);
      navigate(product.route, { replace: true });
    } catch (err) {
      console.warn("[VoxPontOnboarding] failed to save product", err);
      setSavingSlug(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        bgcolor: NAVY_DEEP,
        px: 2,
        py: { xs: 4, sm: 6 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 520 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <VoxPontLogo
            variant="symbol"
            tone="onDark"
            height={56}
            title="VoxPont"
            style={{ display: "flex", justifyContent: "center", width: "100%", margin: "0 auto 14px" }}
          />
          <Typography
            sx={{
              color: GOLD,
              fontWeight: 700,
              fontSize: "1.6rem",
              lineHeight: 1.1,
            }}
          >
            Choose your VoxPont
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.6)", mt: 1, fontSize: "0.95rem" }}
          >
            Pick the product for your setting. This becomes your default — you
            can switch any time from the settings icon.
          </Typography>
        </Box>

        <Paper sx={{ bgcolor: NAVY, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <VoxPontProductPicker
            currentSlug={userData?.voxpontProduct ?? null}
            onSelect={handleSelect}
            saving={Boolean(savingSlug)}
            savingSlug={savingSlug}
            expandable
          />
        </Paper>
      </Box>
    </Box>
  );
}
